import { normalizeIcd10Code, mergeIcd10SearchOptions, icd10ChapterKey } from "./icd10Library";
import { ICD10_SYNONYMS } from "./icd10SynonymMap";

/**
 * Multi-modal ICD-10 search engine over the shared-database index.
 *
 * One query, four ways in: dotted code (E11.9), undotted code (e119, "e11 9"),
 * description text (hypertension), and clinical abbreviations / lay terms
 * (HTN, high blood pressure, presion alta) via a curated synonym map. The
 * engine fans out the corresponding index fetches, then dedupes, ranks, and
 * groups results by 3-character category, using the index's own parent rows
 * (the "E11" row itself) as group headers so the hierarchy is visible.
 *
 * Pure functions + an injected `fetchIcd10` (callers pass api.getIcd10), so
 * tests mock at the api boundary and the engine stays framework-free.
 */

const CODE_RE = /^[A-Z][0-9][0-9A-Z]*$/;

const foldAccents = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const normalizeText = (s) =>
  foldAccents(
    String(s || "")
      .trim()
      .toLowerCase()
  ).replace(/\s+/g, " ");

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Light stemming so an expansion like "hypertension" also hits "hypertensive"
// and "diabetes" hits "diabetic": words longer than 5 chars match on their
// first (length - 3) characters at a word boundary.
const stemOf = (word) => (word.length > 5 ? word.slice(0, word.length - 3) : word);

const phraseMatchesDesc = (phrase, desc) =>
  phrase.split(" ").every((word) => new RegExp("\\b" + escapeRegExp(stemOf(word))).test(desc));

export function classifyIcd10Query(raw) {
  const trimmed = String(raw || "").trim();
  if (!trimmed) return { kind: "empty", codeToken: null, textTokens: [] };
  const tokens = trimmed.split(/\s+/);
  // "e11 9" / "e11.9" / "e119": the whole query is one code, possibly split by
  // spaces or dotted. Joined form must look like letter + digit + alphanumerics
  // and fit an ICD-10-CM code (7 chars max without the dot) — otherwise
  // "e11 diabetes" would join into a fake code token.
  const joined = tokens.join("").replace(/\./g, "").toUpperCase();
  if (joined.length <= 7 && CODE_RE.test(joined)) return { kind: "code", codeToken: joined, textTokens: [] };
  const isCodeToken = (tok) => CODE_RE.test(tok.replace(/\./g, "").toUpperCase());
  const codeToken = tokens.find(isCodeToken) || null;
  const textTokens = tokens.filter((tok) => tok !== codeToken);
  if (codeToken && textTokens.length > 0)
    return { kind: "mixed", codeToken: codeToken.replace(/\./g, "").toUpperCase(), textTokens };
  return { kind: "text", codeToken: null, textTokens: tokens };
}

// "e119" -> ["E11.9", "E119"]; "E11" -> ["E11"]. The index stores codes dotted,
// so the dotted variant is canonical; the compact form still goes out in case a
// table ever stores undotted codes. Works for 7th-character codes (S060X1A ->
// S06.0X1A) and alpha categories (O9A) because nothing past position 2 is
// assumed to be a digit.
export function codeQueryVariants(raw) {
  const compact = String(raw || "")
    .replace(/[.\s]/g, "")
    .toUpperCase();
  if (!CODE_RE.test(compact)) return [];
  if (compact.length <= 3) return [compact];
  return [compact.slice(0, 3) + "." + compact.slice(3), compact];
}

// Extra text queries from the synonym map: whole-phrase lookup first, then a
// single simultaneous token-substitution pass (no combinatorial explosion).
export function expandSynonymQueries(raw) {
  const q = normalizeText(raw);
  if (!q) return [];
  const out = [];
  const push = (s) => {
    if (s && s !== q && !out.includes(s)) out.push(s);
  };
  (ICD10_SYNONYMS[q] || []).forEach(push);
  const tokens = q.split(" ");
  let substituted = false;
  const replaced = tokens.map((tok) => {
    const exp = ICD10_SYNONYMS[tok];
    if (exp && exp.length > 0) {
      substituted = true;
      return exp[0];
    }
    return tok;
  });
  if (substituted) push(replaced.join(" "));
  return out.slice(0, 3);
}

// Score tiers: exact code > code prefix (parents above children) > description
// starts with the query > all query words present > synonym-expansion match
// (tagged with `via` so the UI can say what it matched on) > plain substring.
export function rankIcd10Results(items, query) {
  const cls = classifyIcd10Query(query);
  const variants = cls.codeToken ? codeQueryVariants(cls.codeToken).map(normalizeIcd10Code) : [];
  const rawText = cls.kind === "code" ? "" : normalizeText(query);
  const expansions = expandSynonymQueries(query).map(normalizeText);

  const scored = items.map((item, index) => {
    const code = normalizeIcd10Code(item.n);
    const desc = normalizeText(item.d);
    let score = 200;
    let via = null;
    if (variants.includes(code)) score = 1000;
    else if (variants.some((v) => code.startsWith(v))) score = 900 - Math.min(code.length, 99) / 100;
    else if (rawText && desc.startsWith(rawText)) score = 700;
    else if (rawText && rawText.split(" ").every((w) => new RegExp("\\b" + escapeRegExp(w)).test(desc))) score = 550;
    else {
      via = expansions.find((p) => phraseMatchesDesc(p, desc)) || null;
      if (via) score = 400;
      else if (rawText && desc.includes(rawText)) score = 300;
    }
    return { item: via ? { ...item, via } : item, score, index };
  });

  scored.sort((a, b) => {
    if (a.score !== b.score) return b.score - a.score;
    const ac = a.item.custom ? 1 : 0;
    const bc = b.item.custom ? 1 : 0;
    if (ac !== bc) return bc - ac;
    const byCode = String(a.item.n).localeCompare(String(b.item.n));
    if (byCode !== 0) return byCode;
    return a.index - b.index;
  });
  return scored.map((s) => s.item);
}

// Group a ranked flat list by 3-character category. When the result set
// contains the category's own row (the index ships parent rows), that row
// becomes a pickable group header; otherwise the header is synthetic (root +
// chapter key for the UI to label). Group order follows the ranking.
export function groupIcd10Results(items) {
  const groups = [];
  const byRoot = new Map();
  for (const item of items) {
    if (!item || item.n == null) continue;
    const root = normalizeIcd10Code(item.n).split(".")[0].slice(0, 3);
    let group = byRoot.get(root);
    if (!group) {
      group = { root, header: null, chapterKey: icd10ChapterKey(root), items: [] };
      byRoot.set(root, group);
      groups.push(group);
    }
    if (normalizeIcd10Code(item.n) === root && !group.header) group.header = item;
    else group.items.push(item);
  }
  return groups;
}

// Session-scoped fetch cache (shared by every picker instance). Failed fetches
// are evicted so a transient error doesn't poison the cache.
const fetchCache = new Map();
const FETCH_CACHE_MAX = 100;

export function clearIcd10SearchCache() {
  fetchCache.clear();
}

function cachedFetch(text, fetchIcd10) {
  if (!fetchCache.has(text)) {
    const promise = Promise.resolve()
      .then(() => fetchIcd10(text))
      .then((res) => {
        const result = res?.data?.result;
        return Array.isArray(result) ? result : [];
      })
      .catch((e) => {
        fetchCache.delete(text);
        throw e;
      });
    if (fetchCache.size >= FETCH_CACHE_MAX) fetchCache.delete(fetchCache.keys().next().value);
    fetchCache.set(text, promise);
  }
  return fetchCache.get(text);
}

const dedupeByCode = (items) => {
  const seen = new Set();
  const out = [];
  for (const item of items) {
    if (!item || item.n == null) continue;
    const key = normalizeIcd10Code(item.n);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
};

export async function searchIcd10({ query, fetchIcd10, icd10Library, limit = 50 }) {
  const cls = classifyIcd10Query(query);
  const texts = [];
  if (cls.codeToken) texts.push(...codeQueryVariants(cls.codeToken));
  if (cls.kind !== "code" && cls.kind !== "empty") texts.push(normalizeText(query));
  texts.push(...expandSynonymQueries(query));
  const unique = [...new Set(texts.filter(Boolean))].slice(0, 4);
  if (unique.length === 0) return { groups: [], flat: [], error: false };

  const settled = await Promise.allSettled(unique.map((text) => cachedFetch(text, fetchIcd10)));
  const fetched = settled.flatMap((s) => (s.status === "fulfilled" ? s.value : []));
  const error = settled.every((s) => s.status === "rejected");

  const ranked = rankIcd10Results(dedupeByCode(fetched), query).slice(0, limit);
  const merged = mergeIcd10SearchOptions(ranked, icd10Library, query);
  return { groups: groupIcd10Results(merged), flat: merged, error };
}

// Family browse: every code sharing the 3-character category; when the
// category has no siblings (I10 is alone in its category), widen to the
// 2-character block, the clinical family (I1x = hypertensive diseases).
export async function fetchIcd10Family({ code, fetchIcd10, icd10Library, limit = 50 }) {
  const root = normalizeIcd10Code(code).split(".")[0];
  if (!root) return { groups: [], flat: [], root: "", error: false };
  const matchesForRoot = async (prefix) =>
    (await cachedFetch(prefix, fetchIcd10)).filter((it) => it && normalizeIcd10Code(it.n).startsWith(prefix));
  try {
    let shownRoot = root;
    let items = await matchesForRoot(root);
    if (items.length <= 1 && root.length > 2) {
      shownRoot = root.slice(0, 2);
      items = await matchesForRoot(shownRoot);
    }
    items = [...items].sort((a, b) => String(a.n).localeCompare(String(b.n))).slice(0, limit);
    const merged = mergeIcd10SearchOptions(items, icd10Library, shownRoot);
    return { groups: groupIcd10Results(merged), flat: merged, root: shownRoot, error: false };
  } catch (e) {
    console.error("ICD-10 family lookup failed", e);
    return { groups: [], flat: [], root, error: true };
  }
}

export async function lookupIcd10Code(code, fetchIcd10) {
  const norm = normalizeIcd10Code(code);
  if (!norm) return null;
  try {
    const items = await cachedFetch(norm, fetchIcd10);
    return items.find((it) => it && normalizeIcd10Code(it.n) === norm) || null;
  } catch {
    return null;
  }
}
